/**
 * Assault / boss-fight charger — red line-fill charge (attackRange).
 * Windup: line from head → extended land fills; complete → body dashes to land.
 * Land = aggro hero + landPastHeroPlayFrac × playBounds.width along head→hero
 * (≈ half play view width past the hero), then clamp to playBounds.
 * Hits heroes/thralls along the path during travel. No toughness bar.
 *
 * Victims:
 * - Party heroes (team 0): damage + lateral knock; lethal keeps knock (corpse
 *   flight) — Death event may fire at hit, but view holds death FX until knock ends.
 * - ChargeLethal thralls (team 1 add): wipe + aim-dir death fling with keepKnock.
 *
 * Phaser-free. Numbers from assault.json chargerSkills.
 */

import { clampPointToRect, noteHit } from "../../sim";
import { assaultScript } from "./raid-assault";
import { RaidEvent } from "./raid-events";
import { raidFreezeDiag } from "./raid-freeze-diag";
import { collectRaidDangerZones, raidDangerColor } from "./raid-threat";
import type { RaidBattleState, RaidUnit } from "./raid-types";
import { getActivePlayBounds } from "./sim-lab/sim-stage";
import {
  RaidEnemyDefId,
  RaidEnemyTag,
  enemyHasTag,
} from "./raid-enemy";
import { scaleEnemyOutgoingDamage } from "./raid-escalate";

export function isRaidCharger(u: RaidUnit): boolean {
  return u.defId === RaidEnemyDefId.Charger && u.team === 1;
}

export function livingChargers(state: RaidBattleState): RaidUnit[] {
  return state.units.filter((u) => isRaidCharger(u) && u.hp > 0);
}

function playBounds() {
  return getActivePlayBounds();
}

function clampUnit(u: RaidUnit, x: number, y: number): { x: number; y: number } {
  return clampPointToRect(x, y, playBounds(), u.radius);
}

function dist(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(ax - bx, ay - by);
}

/** Distance from point to segment AB (inclusive endpoints). */
function distPointToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const len2 = abx * abx + aby * aby;
  if (len2 < 1e-8) return dist(px, py, ax, ay);
  let t = ((px - ax) * abx + (py - ay) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  return dist(px, py, ax + abx * t, ay + aby * t);
}

function nearestAlly(state: RaidBattleState, from: RaidUnit): RaidUnit | null {
  let best: RaidUnit | null = null;
  let bestD = Infinity;
  for (const u of state.units) {
    if (u.team !== 0 || u.hp <= 0) continue;
    const d = dist(from.x, from.y, u.x, u.y);
    if (d < bestD) {
      bestD = d;
      best = u;
    }
  }
  return best;
}

function beginLateralChargeKnock(
  u: RaidUnit,
  charger: RaidUnit,
  aimAngle: number,
  knockDist: number,
  dur: number,
  apex: number,
  recoverSec: number,
): void {
  const fx = Math.cos(aimAngle);
  const fy = Math.sin(aimAngle);
  // Perpendicular; pick the side the ally already leans toward (or +perp if centered).
  let side = (u.x - charger.x) * -fy + (u.y - charger.y) * fx;
  if (Math.abs(side) < 1e-3) side = 1;
  const sign = side >= 0 ? 1 : -1;
  const nx = -fy * sign;
  const ny = fx * sign;
  const start = clampUnit(u, u.x, u.y);
  const end = clampUnit(
    u,
    start.x + nx * knockDist,
    start.y + ny * knockDist,
  );
  u.x = start.x;
  u.y = start.y;
  u.vx = 0;
  u.vy = 0;
  u.intentVx = 0;
  u.intentVy = 0;
  u.knock = {
    fromX: start.x,
    fromY: start.y,
    toX: end.x,
    toY: end.y,
    apex: Math.max(0, apex),
    dur: Math.max(0.05, dur),
    t: 0,
    recoverSec: Math.max(0, recoverSec),
  };
}

/** Thrall lethal charge: fling along dash aim (readable scatter, no recover stun). */
function beginChargeDeathFling(
  u: RaidUnit,
  aimAngle: number,
  knockDist: number,
  dur: number,
  apex: number,
): void {
  const nx = Math.cos(aimAngle);
  const ny = Math.sin(aimAngle);
  const start = clampUnit(u, u.x, u.y);
  const end = clampUnit(
    u,
    start.x + nx * knockDist,
    start.y + ny * knockDist,
  );
  u.x = start.x;
  u.y = start.y;
  u.vx = 0;
  u.vy = 0;
  u.intentVx = 0;
  u.intentVy = 0;
  u.knock = {
    fromX: start.x,
    fromY: start.y,
    toX: end.x,
    toY: end.y,
    apex: Math.max(0, apex),
    dur: Math.max(0.05, dur),
    t: 0,
    recoverSec: 0,
  };
}

function finalizeUnitDead(
  state: RaidBattleState,
  u: RaidUnit,
  opts?: { keepKnock?: boolean },
): void {
  u.hp = 0;
  u.vx = 0;
  u.vy = 0;
  u.intentVx = 0;
  u.intentVy = 0;
  u.attackAnimT = 0;
  u.attackCdLeft = 1e9;
  if (!opts?.keepKnock) u.knock = null;
  state.events.push({ kind: RaidEvent.Death, unitId: u.id, team: u.team });
}

function chargeSkill() {
  const script = assaultScript();
  return script.chargerSkills.charge as {
    windupSec: number;
    travelSec: number;
    halfWidth: number;
    damage: number;
    cooldownSec: number;
    danger: number;
    telegraphShape?: string;
    headOffset?: number;
    coneDeg?: number;
    /** Fraction of playBounds width past the hero along charge aim (0.5 ≈ 화면 절반). */
    landPastHeroPlayFrac?: number;
    knockDist?: number;
    knockFlightSec?: number;
    knockApex?: number;
    hitStunSec?: number;
  };
}

/**
 * Aim/charge destination: beyond the target hero along head→hero by
 * `pastFrac × playBounds.width`, then clamp to playBounds (radius pad).
 * If the clamp collapses land onto the body (wall-edge heroes), push to the
 * far edge along aim — then arena center — so the line telegraph never
 * degenerates into a fat disk under the charger (freeze-looking red circle).
 */
export function chargeLandPastHero(
  charger: RaidUnit,
  hero: RaidUnit,
  aim: number,
  pastFrac: number,
  minLandDist = 80,
): { x: number; y: number } {
  const b = playBounds();
  const past = Math.max(0, pastFrac) * (b.xMax - b.xMin);
  const span = Math.hypot(b.xMax - b.xMin, b.yMax - b.yMin);
  let land = clampUnit(
    charger,
    hero.x + Math.cos(aim) * past,
    hero.y + Math.sin(aim) * past,
  );
  if (dist(charger.x, charger.y, land.x, land.y) >= minLandDist) return land;

  land = clampUnit(
    charger,
    charger.x + Math.cos(aim) * span,
    charger.y + Math.sin(aim) * span,
  );
  if (dist(charger.x, charger.y, land.x, land.y) >= minLandDist * 0.5) {
    return land;
  }

  const cx = (b.xMin + b.xMax) / 2;
  const cy = (b.yMin + b.yMax) / 2;
  const aim2 = Math.atan2(cy - charger.y, cx - charger.x);
  if (!Number.isFinite(aim2)) return land;
  return clampUnit(
    charger,
    charger.x + Math.cos(aim2) * span,
    charger.y + Math.sin(aim2) * span,
  );
}

/** Damage a living armed charger. Returns true if dead. */
export function damageCharger(
  state: RaidBattleState,
  charger: RaidUnit,
  amount: number,
  from?: RaidUnit,
  skillId?: string,
): boolean {
  if (!isRaidCharger(charger) || charger.hp <= 0 || amount <= 0) return false;
  if (!charger.combatArmed) return false;
  if (from && from.hp <= 0) return false;
  charger.hp -= amount;
  if (from) {
    noteHit(charger.hit, amount, from.id, state.time);
    state.events.push({
      kind: RaidEvent.Hit,
      x0: from.x,
      y0: from.y,
      x1: charger.x,
      y1: charger.y,
      damage: amount,
      attackerId: from.id,
      targetKind: "unit",
      targetId: charger.id,
      ...(skillId ? { skillId } : {}),
    });
  }
  if (charger.hp <= 0) {
    finalizeUnitDead(state, charger);
    return true;
  }
  return false;
}

/**
 * Charge body volume along the dash segment (prev → current): heroes
 * (lateral knock + stun) and thrall adds (lethal). Skips self / other
 * chargers / brutes so elites don't softlock each other.
 */
function hitUnitsAlongCharge(
  state: RaidBattleState,
  charger: RaidUnit,
  halfWidth: number,
  damage: number,
  aimAngle: number,
  hitIds: Set<number>,
  fromX: number,
  fromY: number,
): number {
  const skills = chargeSkill();
  const knockDist = Number(skills.knockDist) || 52;
  const knockFlight = Number(skills.knockFlightSec) || 0.16;
  const knockApex = Number(skills.knockApex) || 20;
  const hitStun = Number(skills.hitStunSec) || 1.1;
  let hits = 0;
  for (const u of state.units) {
    if (u.hp <= 0 || u.id === charger.id) continue;
    if (hitIds.has(u.id)) continue;
    // Swept capsule: long/fast travel ticks must still clip units on the path.
    const d = distPointToSegment(
      u.x,
      u.y,
      fromX,
      fromY,
      charger.x,
      charger.y,
    );
    if (d > halfWidth + u.radius) continue;

    // Party heroes — shove off the charge line.
    if (u.team === 0) {
      hitIds.add(u.id);
      const dmg = scaleEnemyOutgoingDamage(state, damage);
      u.hp -= dmg;
      noteHit(u.hit, dmg, charger.id, state.time);
      hits += 1;
      beginLateralChargeKnock(
        u,
        charger,
        aimAngle,
        knockDist,
        knockFlight,
        knockApex,
        hitStun,
      );
      state.events.push({
        kind: RaidEvent.Hit,
        x0: charger.x,
        y0: charger.y,
        x1: u.knock?.toX ?? u.x,
        y1: u.knock?.toY ?? u.y,
        damage: dmg,
        attackerId: charger.id,
        targetKind: "unit",
        targetId: u.id,
        skillId: "charge",
      });
      // Lethal: keep lateral knock so corpse flies (same keepKnock as thrall fling).
      // HP/combat truth zeros now; death FX waits for knock end in presentation.
      if (u.hp <= 0) finalizeUnitDead(state, u, { keepKnock: true });
      continue;
    }

    // Same-team chargeLethal trash in the path — wipe + charge-dir death fling.
    if (u.team === 1 && enemyHasTag(u, RaidEnemyTag.ChargeLethal)) {
      hitIds.add(u.id);
      const dealt = Math.max(damage, u.hp);
      u.hp = 0;
      noteHit(u.hit, dealt, charger.id, state.time);
      hits += 1;
      beginChargeDeathFling(u, aimAngle, knockDist, knockFlight, knockApex);
      state.events.push({
        kind: RaidEvent.Hit,
        x0: charger.x,
        y0: charger.y,
        x1: u.knock?.toX ?? u.x,
        y1: u.knock?.toY ?? u.y,
        damage: dealt,
        attackerId: charger.id,
        targetKind: "unit",
        targetId: u.id,
        skillId: "charge",
      });
      finalizeUnitDead(state, u, { keepKnock: true });
    }
  }
  return hits;
}

/** Per-charge hit de-dupe (unit id → allies already struck this dash). */
const chargeHitIds = new Map<number, Set<number>>();

/** Ease-in cubic — plant then accelerate body into the impact point. */
function easeInCubic(u: number): number {
  return u * u * u;
}

function tickOneCharger(
  state: RaidBattleState,
  charger: RaidUnit,
  dt: number,
): void {
  const skills = chargeSkill();
  const range = charger.attackRange;
  const headPad = Math.max(0, Number(skills.headOffset) || 10);

  if (!charger.combatArmed) {
    charger.dropRemain = Math.max(0, charger.dropRemain - dt);
    charger.vx = 0;
    charger.vy = 0;
    charger.intentVx = 0;
    charger.intentVy = 0;
    charger.telegraph = null;
    charger.leap = null;
    chargeHitIds.delete(charger.id);
    if (charger.dropRemain <= 1e-6) {
      charger.combatArmed = true;
      charger.dropRemain = 0;
    }
    return;
  }

  if (state.time < charger.stunnedUntil) {
    charger.telegraph = null;
    charger.leap = null;
    chargeHitIds.delete(charger.id);
    charger.intentVx = 0;
    charger.intentVy = 0;
    charger.vx = 0;
    charger.vy = 0;
    return;
  }

  charger.leapCd = Math.max(0, charger.leapCd - dt);

  // Path telegraph stays for the whole charge (windup + travel). Clearing it on
  // travel_start made the red corridor vanish mid-dash — only clear on travel_end.
  if (charger.leap) {
    const leap = charger.leap;
    leap.remain -= dt;
    if (leap.phase === "windup") {
      charger.intentVx = 0;
      charger.intentVy = 0;
      charger.vx = 0;
      charger.vy = 0;
      if (charger.telegraph) {
        charger.telegraph.remain = leap.remain;
        // Do not re-emit Telegraph every windup tick — open cue is once at cast.
      }
      if (leap.remain <= 1e-6) {
        // Line fill complete — body dash begins; keep full corridor telegraph.
        leap.phase = "travel";
        leap.remain = skills.travelSec;
        leap.duration = skills.travelSec;
        leap.fromX = charger.x;
        leap.fromY = charger.y;
        // remain=0 ⇒ telegraphFillProgress=1. (remain=duration was emptying the fill.)
        if (charger.telegraph) {
          charger.telegraph.remain = 0;
        }
        chargeHitIds.set(charger.id, new Set());
        raidFreezeDiag.noteChargePhase("travel_start", {
          chargerId: charger.id,
          fromX: leap.fromX,
          fromY: leap.fromY,
          landX: leap.landX,
          landY: leap.landY,
          travelSec: skills.travelSec,
          landDist: dist(leap.fromX, leap.fromY, leap.landX, leap.landY),
          halfWidth: skills.halfWidth,
          danger: leap.danger,
          telegraphKept: charger.telegraph != null,
        });
      }
      return;
    }
    // Hold fill at 100% while dashing (do not re-animate fill with travel remain).
    if (charger.telegraph) {
      charger.telegraph.remain = 0;
    }
    const travelDur = Math.max(0.05, leap.duration);
    if (!Number.isFinite(travelDur) || travelDur <= 0) {
      raidFreezeDiag.noteChargePhase("abort_bad_travel", {
        chargerId: charger.id,
        travelDur,
        remain: leap.remain,
      });
      charger.leap = null;
      charger.telegraph = null;
      chargeHitIds.delete(charger.id);
      return;
    }
    const t = 1 - Math.max(0, leap.remain) / travelDur;
    const u = Math.min(1, Math.max(0, t));
    const e = easeInCubic(u);
    const prevX = charger.x;
    const prevY = charger.y;
    const nextX = leap.fromX + (leap.landX - leap.fromX) * e;
    const nextY = leap.fromY + (leap.landY - leap.fromY) * e;
    if (!Number.isFinite(nextX) || !Number.isFinite(nextY)) {
      raidFreezeDiag.noteChargePhase("abort_nan_pos", {
        chargerId: charger.id,
        nextX,
        nextY,
        fromX: leap.fromX,
        fromY: leap.fromY,
        landX: leap.landX,
        landY: leap.landY,
        e,
      });
      const p = clampUnit(charger, leap.landX, leap.landY);
      charger.x = p.x;
      charger.y = p.y;
      charger.leap = null;
      charger.telegraph = null;
      chargeHitIds.delete(charger.id);
      return;
    }
    charger.x = nextX;
    charger.y = nextY;
    const aim = Math.atan2(leap.landY - leap.fromY, leap.landX - leap.fromX);
    let hitBag = chargeHitIds.get(charger.id);
    if (!hitBag) {
      hitBag = new Set();
      chargeHitIds.set(charger.id, hitBag);
    }
    hitUnitsAlongCharge(
      state,
      charger,
      skills.halfWidth,
      skills.damage,
      aim,
      hitBag,
      prevX,
      prevY,
    );
    if (leap.remain <= 1e-6) {
      const p = clampUnit(charger, leap.landX, leap.landY);
      charger.x = p.x;
      charger.y = p.y;
      charger.leap = null;
      charger.telegraph = null;
      charger.leapCd = skills.cooldownSec;
      charger.intentVx = 0;
      charger.intentVy = 0;
      charger.vx = 0;
      charger.vy = 0;
      const nHits = hitBag.size;
      chargeHitIds.delete(charger.id);
      raidFreezeDiag.noteChargePhase("travel_end", {
        chargerId: charger.id,
        x: charger.x,
        y: charger.y,
        hits: nHits,
        danger: leap.danger,
        landRadius: leap.landRadius,
      });
      // No LeapLand — charge is path-hit only; tip circle / land AoE flash must not linger.
    }
    return;
  }

  const nearest = nearestAlly(state, charger);
  if (!nearest) return;

  if (charger.leapCd > 0) return;

  const d = dist(charger.x, charger.y, nearest.x, nearest.y);
  // Gate on attackRange to the sticky/aggro hero (not the extended land).
  if (d > range + nearest.radius) return;

  const aim0 = Math.atan2(nearest.y - charger.y, nearest.x - charger.x);
  if (!Number.isFinite(aim0)) return;
  const pastFrac = skills.landPastHeroPlayFrac ?? 0.5;
  const minLand = Math.max(80, skills.halfWidth * 3);
  const land = chargeLandPastHero(charger, nearest, aim0, pastFrac, minLand);
  if (!Number.isFinite(land.x) || !Number.isFinite(land.y)) return;
  let aim = Math.atan2(land.y - charger.y, land.x - charger.x);
  if (!Number.isFinite(aim)) aim = aim0;
  const landDist = dist(charger.x, charger.y, land.x, land.y);
  if (landDist < skills.halfWidth) {
    raidFreezeDiag.warn("charge_skip_degenerate_land", {
      chargerId: charger.id,
      landDist,
      bodyX: charger.x,
      bodyY: charger.y,
      landX: land.x,
      landY: land.y,
      targetId: nearest.id,
    });
    return;
  }
  const lineLen = Math.max(1, landDist - headPad);
  const windup = skills.windupSec;
  if (!(windup > 0) || !Number.isFinite(windup)) return;
  const travel = skills.travelSec;
  if (!(travel > 0) || !Number.isFinite(travel)) return;
  charger.telegraph = {
    shape: "line",
    range: lineLen,
    coneDeg: skills.coneDeg ?? 0,
    aimAngle: aim,
    remain: windup,
    duration: windup,
    windupSec: windup,
    damage: skills.damage,
    danger: raidDangerColor(skills.danger),
    skillId: "charge",
    halfWidth: skills.halfWidth,
    originPad: headPad,
  };
  charger.leap = {
    phase: "windup",
    remain: windup,
    duration: windup,
    landX: land.x,
    landY: land.y,
    fromX: charger.x,
    fromY: charger.y,
    landRadius: skills.halfWidth,
    landDamage: skills.damage,
    danger: raidDangerColor(skills.danger),
    purpose: "charge",
  };
  charger.intentVx = 0;
  charger.intentVy = 0;
  charger.vx = 0;
  charger.vy = 0;
  charger.aimX = land.x;
  charger.aimY = land.y;
  // Emit open telegraph once (presentation keys on remain≈duration).
  state.events.push({
    kind: RaidEvent.Telegraph,
    shape: "line",
    x: charger.x,
    y: charger.y,
    range: lineLen,
    coneDeg: skills.coneDeg ?? 0,
    aimAngle: aim,
    remain: windup,
    duration: windup,
    danger: raidDangerColor(skills.danger),
    skillId: "charge",
    halfWidth: skills.halfWidth,
    originPad: headPad,
  });
  const zones = collectRaidDangerZones(state.boss, {
    enemies: state.units,
    includeBoss: false,
  });
  const chargeZones = zones.filter(
    (z) => z.skillId === "charge" && z.sourceId === charger.id,
  );
  raidFreezeDiag.noteChargePhase("windup_start", {
    chargerId: charger.id,
    bodyX: charger.x,
    bodyY: charger.y,
    landX: land.x,
    landY: land.y,
    landDist: dist(charger.x, charger.y, land.x, land.y),
    landRadius: skills.halfWidth,
    telegraphShape: "line",
    telegraphRange: lineLen,
    halfWidth: skills.halfWidth,
    originPad: headPad,
    aimAngle: aim,
    danger: raidDangerColor(skills.danger),
    windupSec: windup,
    targetId: nearest.id,
    targetX: nearest.x,
    targetY: nearest.y,
    zones: raidFreezeDiag.snapZones(chargeZones),
  });
}

/** Tick entrance drop + red charge for living chargers. */
export function tickChargerSkills(state: RaidBattleState, dt: number): void {
  for (const u of state.units) {
    if (!isRaidCharger(u) || u.hp <= 0) continue;
    if (!u.combatArmed) {
      tickOneCharger(state, u, dt);
      continue;
    }
    if (u.knock) continue;
    tickOneCharger(state, u, dt);
  }
}
